import { useEffect, useMemo, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import { Sparkle } from "./Decor";

const CYCLE = 16000;

export default function YarnScene() {
  const [paused, setPaused] = useState(false);
  const [cycle, setCycle] = useState(0);
  const [t, setT] = useState(0);

  useEffect(() => {
    if (paused) return undefined;
    const iv = setInterval(() => setT((v) => (v + 200 >= CYCLE ? 0 : v + 200)), 200);
    return () => clearInterval(iv);
  }, [paused, cycle]);

  const phase = t < 5300 ? 0 : t < 10600 ? 1 : 2;
  const labels = useMemo(() => [
    { name: "Yarn", color: "bg-butter-accent/80" },
    { name: "Stitches", color: "bg-sky-200" },
    { name: "Something special", color: "bg-lilac-accent/50" },
  ], []);

  return (
    <div className={`yr-scene ${paused ? "yr-paused" : ""}`}>
      <div className="relative max-w-5xl mx-auto rounded-cloud bg-white/40 border border-white/70 p-4 sm:p-8 overflow-hidden">
        <button
          data-testid="yarn-scene-play-pause"
          onClick={() => setPaused((p) => !p)}
          aria-label={paused ? "Play animation" : "Pause animation"}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 w-14 h-14 rounded-full bg-candy hover:bg-candy-deep text-white shadow-pinkGlow flex items-center justify-center transition-colors duration-200"
        >
          {paused ? <Play className="w-7 h-7 ml-1" /> : <Pause className="w-7 h-7" />}
        </button>
        <button
          data-testid="yarn-scene-replay"
          onClick={() => { setCycle((c) => c + 1); setT(0); setPaused(false); }}
          aria-label="Replay animation"
          className="absolute top-3 right-3 z-20 px-3 py-2 rounded-full bg-white/80 hover:bg-white text-ink text-xs font-bold flex items-center gap-1.5 transition-colors duration-200"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Replay
        </button>

        <div key={cycle} className="relative grid grid-cols-3 items-center gap-2 sm:gap-6 min-h-[200px] sm:min-h-[240px]">
          <div className="yr-anim flex justify-center" style={{ animation: "yr-bob 5s ease-in-out infinite" }}>
            <img src="/assets/yarn-ball.jpg" alt="A ball of pastel yarn" className="w-24 h-24 sm:w-36 sm:h-36 object-cover rounded-full border-4 border-white shadow-card" />
          </div>

          <svg viewBox="0 0 260 160" className="absolute left-1/3 top-1/2 -translate-y-1/2 w-1/3 hidden sm:block z-10" aria-hidden="true">
            <path
              id="yr-thread-path"
              d="M10 20 C 80 10, 140 50, 130 90 S 120 140, 190 130"
              fill="none"
              stroke="#f9a8d4"
              strokeWidth="7"
              strokeLinecap="round"
              strokeDasharray="540"
              className="yr-anim"
              style={{ animation: "yr-thread 16s linear infinite" }}
            />
            <g className="yr-anim" style={{ animation: "yr-hook 16s ease-in-out infinite" }}>
              <line x1="0" y1="0" x2="26" y2="-14" stroke="#ec4899" strokeWidth="8" strokeLinecap="round" />
              <path d="M26 -14 q 14 -8 6 -18" stroke="#a78bfa" strokeWidth="6" fill="none" strokeLinecap="round" />
            </g>
          </svg>

          <div className="yr-anim flex justify-center" style={{ animation: "yr-stitches 16s ease infinite" }}>
            <img src="/assets/stitches.jpg" alt="A growing crochet stitch swatch" className="w-20 h-20 sm:w-32 sm:h-32 object-cover rounded-3xl border-4 border-white shadow-card" />
          </div>

          <div className="relative flex justify-center">
            <div className="yr-anim relative" style={{ animation: "yr-bag 16s ease infinite" }}>
              <img src="/assets/bag-done.jpg" alt="The finished handmade bag" className="w-24 h-24 sm:w-36 sm:h-40 object-cover rounded-cloud border-4 border-white shadow-card" />
              <Sparkle className="yr-anim absolute -top-2 -right-2 w-6 h-6 text-butter-accent" style={{ animation: "yr-sparkle 16s ease infinite" }} />
              <Sparkle className="yr-anim absolute -bottom-1 -left-2 w-4 h-4 text-candy" style={{ animation: "yr-sparkle 16s ease infinite 1s" }} />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mt-6">
          {labels.map((l, i) => (
            <span
              key={l.name}
              data-testid={`yarn-scene-label-${i}`}
              className={`px-4 sm:px-6 py-1.5 rounded-full font-display font-semibold text-sm sm:text-base transition-all duration-500 ${phase === i ? `${l.color} text-ink shadow-pink scale-110` : "bg-white/60 text-ink/50"}`}
            >
              {l.name}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
